import React, { useCallback, useMemo, useState } from 'react';
import PropTypes from 'prop-types';

import clsx from 'clsx';
import i18n from 'i18n-react';
import { withRouter } from 'react-router-dom';
import { HashLink as Link } from 'react-router-hash-link';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

import Progress from 'components/Progress';

import APIHandler from 'config/APIHandler';

import { useSettings } from 'hooks/useSettings';
import { useUserInfo } from 'hooks/useUserInfo';

const Overlay = () => (
  <div className="overlay">
    <FontAwesomeIcon icon={['fas', 'lock']} />
  </div>
);

/**
 * @typedef {object} TrainingExtraCardProps
 * @property {number} appId The application id.
 * @property {object} module The module object. Has similar structure and function to the training chapter object in other API.
 * @property {string} module.image The image url.
 * @property {number} module.id The module id.
 * @property {number} module.percentage The module percentage completed.
 * @property {string} module.title The module title.
 * @property {number} module.durationInMinutes The module estimated duration in minutes.
 * @property {string} redirPath The redirect path.
 * @property {boolean} locked If true, will lock the link. locked links have an overlay and are not clickable.
 * @property {boolean} foundationLocked If true, will render the 'locked' message within the overlay when foundation not
 *   completed.
 * @property {boolean} comingSoon If true, will render the 'coming soon' message within the overlay.
 * @property {boolean} lockedSubtitle If true, will render the 'locked' message within the overlay.
 * @property {string} lockedSubtitleOverride If provided, will override the default 'locked' message with the provided string.
 */

/**
 * A card component, similar to the TrainingCategoryCard, but with different layout and designed specifically for the
 * training modules (or 'subapps') that come from an alternative source and therefore have a slightly different structure.
 *
 * @param {TrainingExtraCardProps} props The training extra card props.
 * @returns {JSX.Element} The training extra card component.
 */
const TrainingExtraCard = ({
  appId,
  module,
  redirPath = '',
  linkPathSegment = 'subapps',
  locked = false,
  foundationLocked = false,
  comingSoon = false,
  lockedSubtitle = false,
  lockedSubtitleOverride = null,
}) => {
  const { lang } = useSettings();
  const { user: { email } } = useUserInfo();

  const { langFile } = lang;
  const locale = `${langFile.toLowerCase()}-${langFile
    .replace('pt', 'BR')
    .replace('es', '41')
    .replace('ko', 'kr')
    .toUpperCase()}`;

  // setLoading is passed to image so that it is considered ' loaded'
  const [loading, setLoading] = useState(false);
  // TODO: Check if durationInMinutes is correctly infered. If so, remove todo. Else, fix it.

  const { id, title, sub_app: subapp } = module;
  let { percentage } = module;
  if (!subapp) {
    percentage = module.category_percentage;
  }

  // ImgIx compression
  let { image } = module;
  image = image
    .replace('front.travpromobile.com', 'travprocdn.imgix.net')
    .replace('s3.amazonaws.com/developertool', 'travprocdn.imgix.net');
  image = `${image}?auto=format&fit=crop&w=500`;

  const cardClasses = clsx('trainingCard', {
    disabled: locked || comingSoon,
    loading: !!loading, // replace with BEM complient versions.
    locked: !!locked, // replace with BEM complient versions.
  });

  const handleLoaded = useCallback(() => {
    setLoading(false);
  }, []);

  const buttonText = useMemo(() => {
    let txt = '';
    switch (percentage) {
      case 100:
        txt = i18n.translate('trainingScreens.review');
        // history push
        break;
      case 0:
        txt = i18n.translate('trainingScreens.start');
        break;
      default:
        txt = i18n.translate('trainingScreens.continue');
    }
    return txt;
  }, [percentage]);

  const linkUrl = useMemo(() => {
    if (redirPath.startsWith('http')) {
      // If redirPath is an external URL, return it as it is
      return redirPath;
    }
    const separator = redirPath.endsWith('/') ? '' : '/';
    if (window.location.pathname.includes('retain')) {
      return `/main/retain/${linkPathSegment}/${id}`;
    }
    if (window.location.pathname.includes('training')) {
      return `/main/training/${linkPathSegment}/${id}`;
    }
    if (percentage < 100) {
      return `/main/training/${linkPathSegment}/${id}`;
    }
    if (percentage >= 100) {
      return `/main/retain/${linkPathSegment}/${id}`;
    }

    return `${redirPath}${separator}${id}`;
  }, [redirPath, linkPathSegment, id]);

  let translatedTitle = title;
  if (langFile !== 'en' && module?.translations?.length > 0) {
    translatedTitle = module.translations.find((t) => t.locale === locale)?.title || title;
  }
  if (langFile !== 'en' && module?.training_translations?.length > 0) {
    translatedTitle = module.training_translations.find((t) => t.locale === locale)?.title || title;
  }
  // console.log('translatedTitle', module);

  // TODO: Cleanup the way this is done. This is a temporary solution.
  if (linkUrl.includes('http')) {
    const { badge } = subapp;
    return (
      <a
        href={linkUrl}
        className={cardClasses}
        {...(locked
          ? {
            onClick: (e) => {
              e.preventDefault();
            },
          } : {}
        )}
        {...(percentage >= 100 
          ? {
            onClick: () => {
              APIHandler.trackEvent(appId, email, 'retain', 'subapp', subapp.id, title).then(() => {});
            }
          } : {}
        )}
      >
        <div className="textContainer">
          {!loading && (
            <div>
              {comingSoon && (
                <h4 className="trainingCard__title">{i18n.translate('trainingScreens.comingSoon', { title })}</h4>
              )}
              {!comingSoon && <h4 className="trainingCard__title">{translatedTitle}</h4>}
              {/* {!locked && (
              <p>
                <FontAwesomeIcon icon={['far', 'clock']} />{' '}
                {i18n.translate('trainingScreens.durationInMinutes', { number: durationInMinutes })}
              </p>
            )} */}
              {!locked && (
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                  }}
                >
                  {buttonText}
                </button>
              )}
              {locked && lockedSubtitle && (
                <p>{lockedSubtitleOverride || i18n.translate('trainingScreens.unlockDefault')}</p>
              )}
            </div>
          )}
        </div>
        <div className="imageContainer">
          {/* TODO: Refactor Image component before implementation at later date */}
          {/* <Image
          appId={appId}
          load={() => {
            setLoading(false);
          }}
          src={image}
          alt={title}
          gradient
          staticImage
        /> */}
          <img src={image} alt={title} onLoad={handleLoaded} />
          {!loading && locked && <Overlay />}
          {!window.location.pathname.includes('retain') && !loading && percentage === 100 && !locked && badge && (
            <div>
              <a href={`https://stack.travpromobile.com/download.php?name=${translatedTitle} badge.png&url=https://cms.travpromobile.com/api/app/diploma?app_id=${subapp.id}`} alt={title} className="badge">
                <FontAwesomeIcon icon={['far', 'file-certificate']} />
                {i18n.translate('trainingScreens.badge')}
              </a>

              <FontAwesomeIcon icon={['far', 'check-circle']} className="chapterComplete" />
            </div>
          )}
        </div>
        {!locked && !loading && <Progress id={`ch${id}`} value={percentage} />}
      </a>
    );
  }

  return (
    <Link
      to={linkUrl}
      className={cardClasses}
      {...(foundationLocked
        ? {
            onClick: (e) => {
              e.preventDefault();
            },
          }
        : {})}
    >
      <div className="textContainer">
        {!loading && (
          <div>
            {comingSoon && (
              <h4 className="trainingCard__title">{i18n.translate('trainingScreens.comingSoon', { title })}</h4>
            )}
            {!comingSoon && <h4 className="trainingCard__title">{translatedTitle}</h4>}
            {/* {!locked && (
              <p>
                <FontAwesomeIcon icon={['far', 'clock']} />{' '}
                {i18n.translate('trainingScreens.durationInMinutes', { number: durationInMinutes })}
              </p>
            )} */}
            {!foundationLocked && (
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                }}
              >
                {buttonText}
              </button>
            )}
            {foundationLocked && <p>{i18n.translate('trainingScreens.unlockFoundation')}</p>}
          </div>
        )}
      </div>
      <div className="imageContainer">
        {/* TODO: Refactor Image component before implementation at later date */}
        {/* <Image
          appId={appId}
          load={() => {
            setLoading(false);
          }}
          src={image}
          alt={title}
          gradient
          staticImage
        /> */}
        <img src={image} alt={title} onLoad={handleLoaded} />
        {!loading && locked && <Overlay />}
        {!loading && percentage === 100 && !locked && subapp && (
          <div>
            <a href={`https://stack.travpromobile.com/download.php?name=${translatedTitle} badge.png&url=https://cms.travpromobile.com/api/app/diploma?app_id=${subapp.id}`} alt={title} className="badge">
              <FontAwesomeIcon icon={['far', 'file-certificate']} />
              {i18n.translate('trainingScreens.badge')}
            </a>

            <FontAwesomeIcon icon={['far', 'check-circle']} className="chapterComplete" />
          </div>
        )}
      </div>
      {!locked && !loading && <Progress id={`ch${id}`} value={percentage} />}
    </Link>
  );
};
export default withRouter(TrainingExtraCard);

TrainingExtraCard.propTypes = {
  appId: PropTypes.number.isRequired,
  module: PropTypes.shape({
    chapterItemCountMin: PropTypes.number,
    id: PropTypes.number,
    image: PropTypes.string,
    badge: PropTypes.string,
    percentage: PropTypes.number,
    title: PropTypes.string.isRequired,
    translations: PropTypes.arrayOf(
      PropTypes.shape({
        locale: PropTypes.string,
        title: PropTypes.string,
      }),
    ),
    training_translations: PropTypes.arrayOf(
      PropTypes.shape({
        locale: PropTypes.string,
        title: PropTypes.string,
      }),
    ),
    progress: PropTypes.shape({
      completed: PropTypes.number,
      total: PropTypes.number,
    }),
    sub_app: PropTypes.PropTypes.shape({
      badge: PropTypes.string,
    }),
    category_percentage: PropTypes.number,
  }).isRequired,
  redirPath: PropTypes.string,
  linkPathSegment: PropTypes.string,
  locked: PropTypes.bool,
  foundationLocked: PropTypes.bool,
  comingSoon: PropTypes.bool,
  lockedSubtitle: PropTypes.bool,
  lockedSubtitleOverride: PropTypes.string,
};
